/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { NavigatorService } from '@services/navigator/navigator.service';
import { AppService } from '@services/app/app.service';
import { Router, Params } from '@angular/router';
import { ERRORS } from '@models/app/errors';
import { WizardStepDTO } from '@api/models/';
import { WizardService } from '@services/wizard/wizard.service';
import { Injectable } from '@angular/core';
import {
  NextDestination,
  StartDestination,
  StepNavPropsModel
} from '@core/models';
import { StepConfigModel } from '@models/wizard/steps-config.model';
import { StepCodes } from '@core/config/step-codes';
import { RouteCodes } from '@core/config/route-codes';

@Injectable({
  providedIn: 'root'
})
export class WizardNavigationService {
  // rotta di navigazione precedente
  private prevNavPath: string;

  private _nextNavProps: StepNavPropsModel;

  get nextNavProps(): StepNavPropsModel {
    return this._nextNavProps ? { ...this._nextNavProps } : undefined;
  }

  constructor(
    private router: Router,
    private navigatorService: NavigatorService,
    private appService: AppService,
    private wizard: WizardService
  ) {}

  /**
   * naviga alla pagina inziale del wizard che può variare a seconda della configurazione
   */
  navigateToStart(
    destination?: StartDestination,
    params?: Params
  ): Promise<void> {
    const startDestination = this.detectStartDestination(destination);

    switch (startDestination) {
      case 'welcome':
        return this.navigateToWelcome();
      case 'summary':
        return this.navigateToSummary();
      case 'first-open-step':
      default:
        return this.navigateToFirstOpenStep(params);
    }
  }

  /**
   * naviga verso la prossima pagina in base alla configurazione
   */
  navigateToNext(
    destination?: NextDestination,
    params?: Params
  ): Promise<void> {
    const nextDestination = this.detectNextDestination(destination);

    switch (nextDestination) {
      case 'first-open-step':
        return this.navigateToFirstOpenStep(params);
      case 'summary':
      default:
        return this.navigateToSummary();
    }
  }

  /**
   * naviga verso il primo step ancora aperto;
   * se non viene trovato naviga verso la fine
   */
  navigateToFirstOpenStep(params?: Params): Promise<void> {
    const step = this.wizard.findStepByIndex(
      this.wizard.findFirstOpenStepIndex()
    );

    if (!!step) {
      return this.navigateToStep(step, params);
    } else {
      return this.navigateToFinish();
    }
  }

  /**
   * naviga verso lo step richiesto
   */
  navigateToStepByCode(stepCode: string, params?: Params): Promise<void> {
    const step = this.wizard.findStepByCode(stepCode);

    return this.navigateToStep(step, params);
  }

  /**
   * naviga alla pagina di benvenuto
   */
  navigateToWelcome(): Promise<void> {
    return this.router.navigateByUrl('/position/welcome').then(() => {});
  }

  /**
   * naviga alla pagina di riepilogo
   */
  navigateToSummary(): Promise<void> {
    return this.router.navigateByUrl('/position/summary').then(() => {});
  }

  /**
   * naviga verso la pagina di uscita dal wizard
   */
  navigateToFinish(): Promise<void> {
    return this.router.navigateByUrl('/position/finish').then(() => {});
  }

  /**
   * naviga verso la pagina di uscita dal wizard
   */
  navigateToExit(reason: string): Promise<void> {
    return this.navigatorService.navigateToExit(reason).then(() => {});
  }

  /**
   * naviga verso la pagina di interruzione
   */
  navigateToStop(): Promise<void> {
    return this.navigatorService
      .navigateToChildRoute(RouteCodes.position, RouteCodes.stop)
      .then(() => {});
  }

  /**
   * naviga verso l'uscita
   */
  navigateToLeave(): Promise<void> {
    if (this.appService.isStandalone()) {
      return this.navigatorService.navigateToReservedArea().then(() => {});
    } else {
      return this.navigateToSummary();
    }
  }

  /**
   * identifica la destinazione per la pagina di inizio
   */
  detectStartDestination(destination?: StartDestination): StartDestination {
    return (
      destination ||
      this.appService.getConfig().wizard.startPage ||
      'first-open-step'
    );
  }

  /**
   * identifica la destinazione per la prossima pagina
   */
  detectNextDestination(destination?: NextDestination): NextDestination {
    return (
      destination || this.appService.getConfig().wizard.nextPage || 'summary'
    );
  }

  /**
   * determina se il back è consentito
   */
  isBackAllowed(): boolean {
    return this.appService.getConfig().wizard.avoidBack !== true;
  }

  /**
   * controlla se le specifiche di navigazione per il prossimo step sono definiti correttamente
   */
  areNextNavPropsDefined(): boolean {
    return (
      !!this.nextNavProps &&
      !!this.nextNavProps.path &&
      !!this.nextNavProps.stepCode
    );
  }

  /**
   * pulisce i dati di navigazione
   */
  cleanNavigationData(): void {
    this.cleanNextNavProps();
    this.cleanPrevNavPath();
  }

  /**
   * naviga verso lo step richiesto
   */
  private navigateToStep(
    step: WizardStepDTO,
    extraParams?: Params
  ): Promise<void> {
    if (!step || !step.stepCode) {
      return Promise.reject(ERRORS.missingPage);
    }

    this.setNextNavProps(step.stepCode, extraParams);

    if (!this.areNextNavPropsDefined()) {
      return Promise.reject(ERRORS.missingPage);
    }

    return this.ensureNavigationEnter()
      .then(() => this.router.navigate([this._nextNavProps.path]))
      .then(() => this.savePrevNavPath())
      .then(() => this.cleanNextNavProps());
  }

  /**
   * se lo step che si sta navigando è sullo stesso path di quello
   * precedente, è necessario prima navigare su una pagina "finta"
   * in modo da innescare comunque l'evento ionViewWillEnter
   */
  private ensureNavigationEnter(): Promise<any> {
    if (this.isNextNavPathSameOfPrev()) {
      return this.navigatorService.navigateToDummy();
    } else {
      return Promise.resolve(true);
    }
  }

  /**
   * controlla se prossima rotta di navigazione è la stessa della precedente
   */
  private isNextNavPathSameOfPrev(): boolean {
    return (
      this.prevNavPath &&
      this._nextNavProps &&
      this.prevNavPath === this._nextNavProps.path
    );
  }

  /**
   * salva la rotta precedente di navigazione a partire dallo stato attuale next nav props
   */
  private savePrevNavPath(): void {
    if (this._nextNavProps && this._nextNavProps.path) {
      this.prevNavPath = this._nextNavProps.path;
    } else {
      this.prevNavPath = null;
    }
  }

  /**
   * imposta le specifiche per la navigazione del prossimo step
   */
  private setNextNavProps(stepCode: string, params?: Params): void {
    if (stepCode) {
      this._nextNavProps = {
        stepCode,
        path: this.defineStepRoutePath(stepCode),
        params: this.defineStepRouteParams(stepCode, params)
      };
    } else {
      this.cleanNextNavProps();
    }
  }

  /**
   * rimuove le specifiche per la navigazione del prossimo step
   */
  private cleanNextNavProps(): void {
    this._nextNavProps = undefined;
  }

  /**
   * rimuove la rotta di navigazione precedente
   */
  private cleanPrevNavPath(): void {
    this.prevNavPath = undefined;
  }

  /**
   * recupera la configurazione dello step
   */
  private getStepRouteConfig(stepCode: string): StepConfigModel {
    const config = this.appService.getStepsConfig()[stepCode];

    if (!config && this.isStepCodeOfDataCollection(stepCode)) {
      return this.appService.getStepsConfig()[StepCodes.dataCollection];
    } else if (!config && this.isStepCodeOfSurveyCollection(stepCode)) {
      return this.appService.getStepsConfig()[StepCodes.surveyCollection];
    } else {
      return config;
    }
  }

  /**
   * stabilisce se lo step è di data collection
   */
  private isStepCodeOfDataCollection(stepCode: string): boolean {
    return stepCode && stepCode.toLowerCase().indexOf('form') > -1;
  }

  /**
   * stabilisce se lo step è di survey collection
   */
  private isStepCodeOfSurveyCollection(stepCode: string): boolean {
    return stepCode && stepCode.toLowerCase().indexOf('survey') > -1;
  }

  /**
   * definisce il path della rotta
   */
  private defineStepRoutePath(stepCode: string): string {
    const config = this.getStepRouteConfig(stepCode);

    if (config && config.absolutePath !== true) {
      return '/position/' + config.path;
    } else if (config && config.path) {
      return config.path;
    }
  }

  /**
   * definisce i parametri per la rotta
   */
  private defineStepRouteParams(stepCode: string, params?: Params): Params {
    const config = this.getStepRouteConfig(stepCode);

    if (config && !!config.params && params) {
      return { ...config.params, ...params };
    } else if (config && !!config.params) {
      return { ...config.params };
    } else if (!!params) {
      return { ...params };
    } else {
      return {};
    }
  }
}
